import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { primaryButtonSx } from '../../../styles/adminPageStyles';
import {
  Box,
  Button,
  Paper,
  Stack,
  Chip,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Typography,
  Divider,
  Alert,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Link,
} from '@mui/material';
import {
  Add as AddIcon,
  Settings as SettingsIcon,
  Refresh as RefreshIcon,
  FilterList as FilterListIcon,
  Public as PublicIcon,
  ExpandMore as ExpandMoreIcon,
  EventNote as EventNoteIcon,
  SelectAll as SelectAllIcon,
  Deselect as DeselectIcon,
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import interactionPlugin from '@fullcalendar/interaction';
import type { EventInput, EventClickArg, DateSelectArg, EventDropArg } from '@fullcalendar/core';
import PageHeader from '../../../components/PageHeader';
import calendarApi, {
  AggregatedEvent,
  BookingType,
  CalendarSettings,
} from './calendarApi';
import BookingDrawer from './BookingDrawer';
import {
  resolveAdminUiLabel,
  resolveCalendarOverlayLabel,
  type AdminUiLabelKey,
} from '../../../constants/adminUiLabels';
import {
  BusinessFeaturePermissions,
  fetchBusinessCalendarBootstrap,
  getCalendarOverlayOptions,
} from '../../../utils/businessPermissions';

const CalendarPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const calendarRef = useRef<FullCalendar>(null);

  const [settings, setSettings] = useState<CalendarSettings | null>(null);
  const [types, setTypes] = useState<BookingType[]>([]);
  const [events, setEvents] = useState<AggregatedEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [featurePermissions, setFeaturePermissions] = useState<BusinessFeaturePermissions | null>(null);
  const [adminUiLabels, setAdminUiLabels] = useState<Partial<Record<AdminUiLabelKey, string>>>({});

  const overlayOptions = useMemo(() => {
    if (!featurePermissions) return [];
    return getCalendarOverlayOptions(featurePermissions).map((o) => {
      if (o.kind === 'shift') return o;
      return {
        ...o,
        label: resolveCalendarOverlayLabel(o.kind, adminUiLabels),
      };
    });
  }, [featurePermissions, adminUiLabels]);

  const [enabledKinds, setEnabledKinds] = useState<Record<string, boolean>>({});
  const [typeFilter, setTypeFilter] = useState<Record<string, boolean>>({});
  const [view, setView] = useState<'dayGridMonth' | 'timeGridWeek' | 'timeGridDay' | 'listWeek'>('dayGridMonth');

  const [drawerBookingId, setDrawerBookingId] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerInitial, setDrawerInitial] = useState<{ startAt?: string; endAt?: string } | null>(null);

  const [currentRange, setCurrentRange] = useState<{ from: string; to: string } | null>(null);

  const loadInitial = useCallback(async () => {
    try {
      const [s, t, bootstrap] = await Promise.all([
        calendarApi.getSettings(),
        calendarApi.getBookingTypes(),
        fetchBusinessCalendarBootstrap(),
      ]);
      setSettings(s);
      setTypes(t);
      setFeaturePermissions(bootstrap.permissions);
      setAdminUiLabels(bootstrap.adminUiLabels);
      const overlays = getCalendarOverlayOptions(bootstrap.permissions);
      setEnabledKinds(
        overlays.reduce((acc, o) => ({ ...acc, [o.kind]: true }), {} as Record<string, boolean>),
      );
      setTypeFilter((prev) => {
        const next = { ...prev };
        for (const ty of t) if (!(ty.type_id in next)) next[ty.type_id] = true;
        return next;
      });
    } catch (e: any) {
      setError(e.message || 'Failed to load calendar');
    }
  }, []);

  useEffect(() => { loadInitial(); }, [loadInitial]);

  const loadEvents = useCallback(async () => {
    if (!currentRange) return;
    setLoading(true);
    try {
      const evs = await calendarApi.aggregated({
        from: currentRange.from,
        to: currentRange.to,
        includeOverlays: true,
      });
      setEvents(evs);
    } catch (e: any) {
      setError(e.message || 'Failed to load events');
    } finally {
      setLoading(false);
    }
  }, [currentRange]);

  useEffect(() => { loadEvents(); }, [loadEvents]);

  // Open booking drawer when ?booking=<id> is in the URL (used by deep links)
  useEffect(() => {
    const bid = searchParams.get('booking');
    if (bid) {
      setDrawerBookingId(bid);
      setDrawerOpen(true);
    }
  }, [searchParams]);

  const allowedKindSet = useMemo(
    () => new Set(overlayOptions.map((o) => o.kind)),
    [overlayOptions],
  );

  const fcEvents = useMemo<EventInput[]>(() => {
    return events
      .filter((e) => allowedKindSet.has(e.kind) && enabledKinds[e.kind] !== false)
      .filter((e) => e.kind !== 'booking' || !e.typeId || typeFilter[e.typeId] !== false)
      .map((e) => ({
        id: `${e.kind}:${e.id}`,
        title: e.title,
        start: e.start,
        end: e.end,
        allDay: e.allDay,
        backgroundColor: e.color,
        borderColor: e.color,
        textColor: '#fff',
        extendedProps: { kind: e.kind, status: e.status, originalId: e.id, typeName: e.typeName },
        editable: e.kind === 'booking' && e.status !== 'cancelled' && e.status !== 'completed',
      }));
  }, [events, enabledKinds, typeFilter, allowedKindSet]);

  const handleDatesSet = useCallback((arg: { start: Date; end: Date; view: { type: string } }) => {
    setCurrentRange({ from: arg.start.toISOString(), to: arg.end.toISOString() });
    setView(arg.view.type as any);
  }, []);

  const handleSelect = useCallback((arg: DateSelectArg) => {
    setDrawerBookingId(null);
    setDrawerInitial({ startAt: arg.start.toISOString(), endAt: arg.end.toISOString() });
    setDrawerOpen(true);
  }, []);

  const handleEventClick = useCallback((arg: EventClickArg) => {
    const kind = arg.event.extendedProps.kind as AggregatedEvent['kind'];
    const id = arg.event.extendedProps.originalId as string;
    if (kind === 'booking') {
      setDrawerInitial(null);
      setDrawerBookingId(id);
      setDrawerOpen(true);
      setSearchParams({ booking: id });
    } else if (kind === 'safety_check') {
      navigate(`/admin/safety-checks?tab=responses&safety_check_id=${encodeURIComponent(id)}`);
    } else if (kind === 'loan_return') {
      navigate('/admin/loan-checks?tab=responses');
    } else if (kind === 'outgoing_delivery') {
      navigate(`/admin/delivery-checks?tab=outgoing&request_id=${encodeURIComponent(id)}`);
    } else if (kind === 'shift') {
      navigate(`/admin/hr?focus=${id}`);
    }
  }, [navigate, setSearchParams]);

  const handleEventDrop = useCallback(async (arg: EventDropArg) => {
    const kind = arg.event.extendedProps.kind as AggregatedEvent['kind'];
    if (kind !== 'booking' || !arg.event.start) {
      arg.revert();
      return;
    }
    try {
      await calendarApi.updateBooking(arg.event.extendedProps.originalId as string, {
        startAt: arg.event.start.toISOString(),
        endAt: (arg.event.end || arg.event.start).toISOString(),
      });
      await loadEvents();
    } catch (e: any) {
      setError(e.message || 'Failed to reschedule booking');
      arg.revert();
    }
  }, [loadEvents]);

  const handleEventResize = useCallback(async (arg: any) => {
    const kind = arg.event.extendedProps.kind as AggregatedEvent['kind'];
    if (kind !== 'booking' || !arg.event.start) { arg.revert(); return; }
    try {
      await calendarApi.updateBooking(arg.event.extendedProps.originalId as string, {
        startAt: arg.event.start.toISOString(),
        endAt: (arg.event.end || arg.event.start).toISOString(),
      });
      await loadEvents();
    } catch (e: any) {
      setError(e.message || 'Failed to resize booking');
      arg.revert();
    }
  }, [loadEvents]);

  const closeDrawer = useCallback(() => {
    setDrawerOpen(false);
    setDrawerBookingId(null);
    setDrawerInitial(null);
    if (searchParams.get('booking')) {
      searchParams.delete('booking');
      setSearchParams(searchParams);
    }
  }, [searchParams, setSearchParams]);

  const setAllOverlays = (on: boolean) => {
    setEnabledKinds(
      overlayOptions.reduce((acc, o) => ({ ...acc, [o.kind]: on }), {} as Record<string, boolean>),
    );
  };

  const setAllTypes = (on: boolean) => {
    setTypeFilter(types.reduce((acc, t) => ({ ...acc, [t.type_id]: on }), {} as Record<string, boolean>));
  };

  const visibleCount = fcEvents.length;

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', pt: 0.5, pb: 3 }}>
      <PageHeader
        title="Calendar"
        subtitle="Bookings and schedules from the modules enabled for your business"
        action={
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              variant="outlined"
              startIcon={<PublicIcon />}
              onClick={() => navigate('/admin/calendar/settings')}
              disabled={!settings}
              sx={primaryButtonSx}
            >
              {settings?.publicBookingEnabled ? 'Public link live' : 'Set up public link'}
            </Button>
            <Button
              variant="outlined"
              startIcon={<SettingsIcon />}
              onClick={() => navigate('/admin/calendar/settings')}
              sx={primaryButtonSx}
            >
              Settings
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setDrawerBookingId(null);
                setDrawerInitial(null);
                setDrawerOpen(true);
              }}
              sx={primaryButtonSx}
            >
              New booking
            </Button>
          </Stack>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexDirection: { xs: 'column', md: 'row' } }}>
        {/* Left rail filters */}
        <Paper
          sx={{
            width: { xs: '100%', md: 280 },
            flexShrink: 0,
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            overflow: 'hidden',
            position: { md: 'sticky' },
            top: { md: 88 },
            alignSelf: { md: 'flex-start' },
            maxHeight: { md: 'calc(100vh - 120px)' },
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box sx={{ px: 2, py: 1.5, bgcolor: 'grey.50', borderBottom: 1, borderColor: 'divider' }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography variant="subtitle2" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <FilterListIcon fontSize="small" color="primary" />
                Filters
              </Typography>
              <Chip size="small" label={`${visibleCount} visible`} color="primary" variant="outlined" />
            </Stack>
          </Box>

          <Box sx={{ overflowY: 'auto', flex: 1, px: 0.5 }}>
            {overlayOptions.length > 1 && (
              <Accordion defaultExpanded disableGutters elevation={0} sx={{ '&:before': { display: 'none' } }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 44, px: 1.5 }}>
                  <Typography variant="body2" fontWeight={600}>Schedule overlays</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0, px: 1.5, pb: 1 }}>
                  <Stack direction="row" spacing={0.5} sx={{ mb: 1 }}>
                    <Button size="small" startIcon={<SelectAllIcon />} onClick={() => setAllOverlays(true)} sx={{ minWidth: 0, flex: 1, fontSize: '0.7rem' }}>
                      All
                    </Button>
                    <Button size="small" startIcon={<DeselectIcon />} onClick={() => setAllOverlays(false)} sx={{ minWidth: 0, flex: 1, fontSize: '0.7rem' }}>
                      None
                    </Button>
                  </Stack>
                  <FormGroup>
                    {overlayOptions.map((o) => (
                      <FormControlLabel
                        key={o.kind}
                        sx={{ ml: 0, mr: 0 }}
                        control={
                          <Checkbox
                            size="small"
                            checked={enabledKinds[o.kind] !== false}
                            onChange={(_, v) => setEnabledKinds((prev) => ({ ...prev, [o.kind]: v }))}
                            sx={{ color: o.color, '&.Mui-checked': { color: o.color } }}
                          />
                        }
                        label={
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: o.color, flexShrink: 0 }} />
                            <Typography variant="body2">{o.label}</Typography>
                          </Stack>
                        }
                      />
                    ))}
                  </FormGroup>
                </AccordionDetails>
              </Accordion>
            )}

            <Accordion defaultExpanded disableGutters elevation={0} sx={{ '&:before': { display: 'none' } }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 44, px: 1.5 }}>
                <Typography variant="body2" fontWeight={600}>Booking types</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0, px: 1.5, pb: 1 }}>
                {types.length === 0 ? (
                  <Typography variant="caption" color="text.secondary">
                    No types yet.{' '}
                    <Link component="button" variant="caption" onClick={() => navigate('/admin/calendar/settings')}>
                      Add in settings
                    </Link>
                  </Typography>
                ) : (
                  <>
                    <Stack direction="row" spacing={0.5} sx={{ mb: 1 }}>
                      <Button size="small" onClick={() => setAllTypes(true)} sx={{ minWidth: 0, flex: 1, fontSize: '0.7rem' }}>All</Button>
                      <Button size="small" onClick={() => setAllTypes(false)} sx={{ minWidth: 0, flex: 1, fontSize: '0.7rem' }}>None</Button>
                    </Stack>
                    <FormGroup>
                      {types.map((t) => (
                        <FormControlLabel
                          key={t.type_id}
                          sx={{ ml: 0, mr: 0, alignItems: 'flex-start' }}
                          control={
                            <Checkbox
                              size="small"
                              checked={typeFilter[t.type_id] !== false}
                              onChange={(_, v) => setTypeFilter((prev) => ({ ...prev, [t.type_id]: v }))}
                              sx={{ color: t.color, '&.Mui-checked': { color: t.color }, pt: 0.5 }}
                            />
                          }
                          label={
                            <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" sx={{ pt: 0.25 }}>
                              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: t.color }} />
                              <Typography variant="body2">{t.name}</Typography>
                              {t.is_public_bookable ? (
                                <Chip label="Public" size="small" color="info" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
                              ) : null}
                            </Stack>
                          }
                        />
                      ))}
                    </FormGroup>
                  </>
                )}
              </AccordionDetails>
            </Accordion>
          </Box>

          <Divider />
          <Stack spacing={0.75} sx={{ p: 1.5, bgcolor: 'grey.50' }}>
            <Button size="small" startIcon={<RefreshIcon />} onClick={loadEvents} disabled={loading} fullWidth variant="outlined">
              {loading ? 'Loading…' : 'Refresh calendar'}
            </Button>
            <Button size="small" startIcon={<EventNoteIcon />} fullWidth variant="text" onClick={() => navigate('/admin/calendar/bookings')}>
              {resolveAdminUiLabel('nav_calendar_bookings', adminUiLabels)}
            </Button>
            <Button size="small" startIcon={<SettingsIcon />} fullWidth variant="text" onClick={() => navigate('/admin/calendar/settings')}>
              {resolveAdminUiLabel('nav_calendar_settings', adminUiLabels)}
            </Button>
            {settings?.publicBookingEnabled && settings.businessSlug ? (
              <Button
                size="small"
                variant="contained"
                startIcon={<PublicIcon />}
                fullWidth
                onClick={() => window.open(`/${settings.businessSlug}/booking`, '_blank')}
              >
                Open public page
              </Button>
            ) : null}
          </Stack>
        </Paper>

        {/* Calendar */}
        <Paper sx={{ flex: 1, p: 2, borderRadius: 3, width: '100%' }}>
          <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
            <ToggleButtonGroup
              value={view}
              exclusive
              size="small"
              onChange={(_, v) => {
                if (!v) return;
                setView(v);
                const api = calendarRef.current?.getApi();
                api?.changeView(v);
              }}
            >
              <ToggleButton value="dayGridMonth">Month</ToggleButton>
              <ToggleButton value="timeGridWeek">Week</ToggleButton>
              <ToggleButton value="timeGridDay">Day</ToggleButton>
              <ToggleButton value="listWeek">Agenda</ToggleButton>
            </ToggleButtonGroup>
          </Stack>
          {loading && <CircularProgress size={20} sx={{ position: 'absolute', m: 2 }} />}
          <Box sx={{
            '& .fc': { fontFamily: 'inherit' },
            '& .fc-toolbar-title': { fontSize: '1.1rem', fontWeight: 600 },
            '& .fc-button-primary': { backgroundColor: '#2563eb', borderColor: '#2563eb' },
            '& .fc-event': { cursor: 'pointer', borderRadius: 6, padding: '2px 4px' },
          }}>
            <FullCalendar
              ref={calendarRef}
              plugins={[dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin]}
              initialView={view}
              headerToolbar={{ left: 'prev,next today', center: 'title', right: '' }}
              events={fcEvents}
              datesSet={handleDatesSet}
              selectable
              selectMirror
              editable
              eventClick={handleEventClick}
              select={handleSelect}
              eventDrop={handleEventDrop}
              eventResize={handleEventResize}
              height="auto"
              nowIndicator
              weekNumbers={false}
              firstDay={1}
              dayMaxEvents
              expandRows
            />
          </Box>
        </Paper>
      </Box>

      <BookingDrawer
        open={drawerOpen}
        bookingId={drawerBookingId}
        initialStartAt={drawerInitial?.startAt}
        initialEndAt={drawerInitial?.endAt}
        bookingTypes={types}
        onClose={closeDrawer}
        onSaved={() => { loadEvents(); }}
        onDeleted={() => { loadEvents(); closeDrawer(); }}
      />
    </Box>
  );
};

export default CalendarPage;
